import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AlertTriangle, ChevronRight, Copy, ExternalLink } from "lucide-react";
import {
  apiDocsUrls,
  integrationsApi,
  integrationsQueryKeys,
  scopeLabel,
} from "../../shared/api/integrations";
import { apiError } from "../../shared/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { copyText } from "../../shared/clipboard";

/**
 * T89 — Tích hợp. The single HIS-side AI surface (ADR-0016).
 *
 * In scope: connect/disconnect Addy AI Assistant, issue/rotate the HIS-issued
 * service-account API key, toggle the fixed 8 scopes, link to the OpenAPI
 * docs, link to the AI audit trail.
 *
 * Deliberately NOT here (ADR-0016 locked): Zalo OA config, conversation /
 * training / prompt / persona UI — those live in the separate Addy AI
 * Assistant product. Pro connectors (BHYT, đơn thuốc quốc gia, e-invoice)
 * are out of Lite scope. Shown as informational cards, not config.
 */
export function IntegrationsPage() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: integrationsQueryKeys.status(),
    queryFn: () => integrationsApi.get(),
  });
  const catalog = useQuery({
    queryKey: integrationsQueryKeys.scopeCatalog(),
    queryFn: () => integrationsApi.scopeCatalog(),
  });

  const [instanceUrl, setInstanceUrl] = useState("");
  const [scopeDraft, setScopeDraft] = useState<string[] | null>(null);
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status.data && instanceUrl === "")
      setInstanceUrl(status.data.instanceUrl ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data]);

  const conn = status.data;
  const connected = conn?.status === "CONNECTED";
  const scopes = scopeDraft ?? conn?.enabledScopes ?? [];
  const scopesDirty =
    scopeDraft != null &&
    conn != null &&
    JSON.stringify([...scopeDraft].sort()) !==
      JSON.stringify([...conn.enabledScopes].sort());

  function refresh() {
    qc.invalidateQueries({ queryKey: integrationsQueryKeys.status() });
  }

  const connect = useMutation({
    mutationFn: () => integrationsApi.connect(instanceUrl),
    onSuccess: (d) => {
      setIssuedKey(d.apiKeyPlaintext ?? null);
      setError(null);
      refresh();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });
  const regenerate = useMutation({
    mutationFn: () => integrationsApi.regenerateKey(),
    onSuccess: (d) => {
      setIssuedKey(d.apiKeyPlaintext ?? null);
      refresh();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });
  const disconnect = useMutation({
    mutationFn: () => integrationsApi.disconnect(),
    onSuccess: () => {
      setIssuedKey(null);
      refresh();
    },
  });
  const saveScopes = useMutation({
    mutationFn: () => integrationsApi.updateScopes(scopeDraft ?? []),
    onSuccess: () => {
      setScopeDraft(null);
      setError(null);
      refresh();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  function toggleScope(s: string) {
    const base = scopeDraft ?? conn?.enabledScopes ?? [];
    setScopeDraft(
      base.includes(s) ? base.filter((x) => x !== s) : [...base, s]
    );
  }

  const docs = apiDocsUrls();

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Tích hợp
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Kết nối Addy AI Assistant (sản phẩm độc lập) — cấp khoá API để hệ
          thống bên ngoài gọi vào các API đã định, bật/tắt từng quyền, và xem
          nhật ký truy cập.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      {/* ---- Connection ---- */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Addy AI Assistant</CardTitle>
          <Badge variant={connected ? "success" : "muted"}>
            {connected ? "Đã kết nối" : "Chưa kết nối"}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-3">
          {status.isLoading && (
            <p className="text-sm text-muted-foreground">Đang tải…</p>
          )}

          <div className="max-w-xl space-y-1.5">
            <Label htmlFor="instanceUrl">Địa chỉ instance AI Assistant</Label>
            <Input
              id="instanceUrl"
              type="url"
              placeholder="https://ai.addy.vn/t/phongkham"
              value={instanceUrl}
              onChange={(e) => setInstanceUrl(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {!connected ? (
              <Button
                type="button"
                onClick={() => connect.mutate()}
                disabled={connect.isPending}
              >
                {connect.isPending
                  ? "Đang kết nối…"
                  : "Kết nối & cấp khoá API"}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => regenerate.mutate()}
                  disabled={regenerate.isPending}
                >
                  Tạo lại khoá API
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (
                      window.confirm(
                        "Ngắt kết nối sẽ thu hồi khoá API. AI Assistant sẽ không gọi được nữa. Tiếp tục?"
                      )
                    )
                      disconnect.mutate();
                  }}
                  disabled={disconnect.isPending}
                >
                  Ngắt kết nối
                </Button>
              </>
            )}
          </div>

          {conn?.apiKeyPrefix && !issuedKey && (
            <p className="text-[13px] text-muted-foreground">
              Khoá hiện tại:{" "}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                {conn.apiKeyPrefix}
              </code>{" "}
              — chỉ hiển thị đầy đủ một lần khi tạo. Mất khoá thì bấm “Tạo lại
              khoá API”.
            </p>
          )}

          {issuedKey && (
            <div
              role="alert"
              className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            >
              <strong>Khoá API (chỉ hiển thị một lần):</strong>
              <pre className="select-all overflow-x-auto rounded-md bg-muted p-2 font-mono text-xs text-foreground">
                {issuedKey}
              </pre>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void copyText(issuedKey);
                }}
              >
                <Copy className="mr-1.5 h-4 w-4" />
                Sao chép
              </Button>
              <div className="text-[13px]">
                Dán khoá này + địa chỉ HIS vào phần kết nối của sản phẩm Addy AI
                Assistant. HIS không lưu lại bản rõ.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---- Scopes ---- */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Quyền truy cập (8 scope cố định — ADR-0016)</CardTitle>
          <Button
            type="button"
            onClick={() => saveScopes.mutate()}
            disabled={!scopesDirty || saveScopes.isPending}
          >
            {saveScopes.isPending ? "Đang lưu…" : "Lưu quyền"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <ScopeGroup
            title="Không chứa thông tin bệnh nhân (PHI-free)"
            hint="An toàn để bật mặc định."
            scopes={catalog.data?.phiFree ?? []}
            enabled={scopes}
            onToggle={toggleScope}
          />
          <ScopeGroup
            title="Có chạm dữ liệu bệnh nhân (PHI-present)"
            hint="Chỉ bật sau khi phòng khám hoàn tất quy trình đồng ý (Decree 13/2023). Mỗi lần gọi đều được ghi nhật ký."
            scopes={catalog.data?.phiPresent ?? []}
            enabled={scopes}
            onToggle={toggleScope}
            warn
          />
        </CardContent>
      </Card>

      {/* ---- API for external systems ---- */}
      <Card>
        <CardHeader>
          <CardTitle>API cho hệ thống bên ngoài</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Bên thứ ba (Addy AI Assistant hoặc hệ thống khác) gọi vào{" "}
            <strong className="text-foreground">8 endpoint đã định</strong> dưới{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              /api/v1/ai/*
            </code>{" "}
            bằng khoá API ở trên. Mỗi request phải kèm header:{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              X-Initiated-By: ai-assistant
            </code>
            ,{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              X-Service-Account
            </code>
            ,{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              X-AI-Session-Id
            </code>{" "}
            — toàn bộ được ghi vào nhật ký kiểm toán với{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              actor_type = SERVICE_ACCOUNT
            </code>
            .
          </p>
          <ul className="space-y-1">
            <li className="flex flex-wrap items-center gap-1">
              Tài liệu API (Swagger UI):{" "}
              <a
                href={docs.swaggerUi}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 break-all font-medium text-primary hover:underline"
              >
                {docs.swaggerUi}
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            </li>
            <li className="flex flex-wrap items-center gap-1">
              OpenAPI JSON:{" "}
              <a
                href={docs.openapiJson}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 break-all font-medium text-primary hover:underline"
              >
                {docs.openapiJson}
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            </li>
          </ul>
          <details className="text-[13px]">
            <summary className="cursor-pointer text-foreground">
              Ví dụ: lấy khung giờ trống + đặt lịch hẹn
            </summary>
            <pre className="mt-2 overflow-x-auto rounded-md bg-muted p-2.5 font-mono text-xs">{`curl -H "Authorization: Bearer <API_KEY>" \\
     -H "X-Initiated-By: ai-assistant" \\
     "<HIS_URL>/api/v1/ai/slots?doctorId=...&from=...&to=..."

curl -X POST -H "Authorization: Bearer <API_KEY>" \\
     -H "X-Initiated-By: ai-assistant" \\
     -H "Content-Type: application/json" \\
     -d '{"intakeDraftId":"...","slotId":"..."}' \\
     "<HIS_URL>/api/v1/ai/appointments/request"`}</pre>
          </details>
          <p className="text-[13px] text-muted-foreground">
            Lưu ý: lấy <em>kết quả khám / hồ sơ bệnh án đầy đủ</em> KHÔNG nằm
            trong 8 scope hiện tại (ADR-0016 — kê đơn, ghi chú khám, đọc hồ sơ
            đầy đủ bị cấm cho AI). Mở rộng scope cần một ADR addendum, không bật
            được từ màn hình này.
          </p>
        </CardContent>
      </Card>

      {/* ---- Audit link ---- */}
      <Card>
        <CardHeader>
          <CardTitle>Nhật ký truy cập của AI</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Mọi lời gọi từ AI Assistant được ghi với{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              actor_type = SERVICE_ACCOUNT
            </code>{" "}
            kèm AI session id.
          </p>
          <Button asChild variant="outline">
            <Link to="/settings/audit-log">
              Mở nhật ký kiểm toán
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* ---- Zalo OA — boundary card, NOT config ---- */}
      <Card>
        <CardHeader>
          <CardTitle>Zalo OA</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p>
            Kết nối Zalo OA, token và luồng hội thoại được cấu hình trong{" "}
            <strong>sản phẩm Addy AI Assistant</strong>, không phải trong HIS
            (ADR-0016 — HIS không giữ token/cấu hình Zalo). Sau khi kết nối AI
            Assistant ở trên, hãy cấu hình Zalo OA trong bảng điều khiển của Addy
            AI Assistant; các yêu cầu đến từ Zalo vẫn đi qua đúng 8 scope và được
            ghi nhật ký như trên.
          </p>
        </CardContent>
      </Card>

      {/* ---- Pro connectors — informational ---- */}
      <Card className="bg-muted/40">
        <CardHeader>
          <CardTitle>Tích hợp gói Pro</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p className="text-muted-foreground">
            BHYT, Đơn thuốc quốc gia (donthuocquocgia.vn), Hoá đơn điện tử
            (Misa/Viettel) — thuộc gói <strong className="text-foreground">Pro</strong>, chưa
            có trong gói Lite. Liên hệ Addy để nâng cấp.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ScopeGroup({
  title,
  hint,
  scopes,
  enabled,
  onToggle,
  warn,
}: {
  title: string;
  hint: string;
  scopes: string[];
  enabled: string[];
  onToggle: (s: string) => void;
  warn?: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="space-y-0.5">
        <h3 className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          {warn && <AlertTriangle className="h-4 w-4 text-destructive" />}
          {title}
        </h3>
        <p
          className={
            warn
              ? "text-xs text-destructive"
              : "text-xs text-muted-foreground"
          }
        >
          {hint}
        </p>
      </div>
      <div className="grid gap-2">
        {scopes.map((s) => (
          <Label
            key={s}
            className="flex items-center gap-2 font-normal"
          >
            <Checkbox
              checked={enabled.includes(s)}
              onCheckedChange={() => onToggle(s)}
            />
            <span>
              {scopeLabel(s)}{" "}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                {s}
              </code>
            </span>
          </Label>
        ))}
      </div>
    </div>
  );
}
